import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { FaCreditCard, FaTrash, FaPlus, FaLock, FaCheck, FaTimes, FaUser, FaEuroSign } from 'react-icons/fa';
import api from '../../../services/api';

// Charger Stripe avec la clé publique
const stripePromise = loadStripe(
  process.env.REACT_APP_ENV === "production"
    ? "pk_live_51PLuQiHzL8gubNCfOBaaohZ0Vwz2Rti3HVD5YSdxui9saiRuPA1T0XRfNKEfvmlxSeKBHJX3uGBcOGdd769RoChF00t2XrnVLW"
    : "pk_test_51PLuQiHzL8gubNCfacSYt0jDJM3KgLsMpR3lYmVdKJTu57m1WCbR9NUnIIbKZeLt7Jlf8rZ1CQyH57REdDy6Z5wU00h2owk64H"
);

// Style pour le CardElement
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

// Composant interne pour le formulaire de carte
const CardForm = ({ onCardSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Créer un SetupIntent via notre endpoint free-amount
      const { data } = await api.post('/free-amount/setup-intent');
      const { clientSecret } = data;

      // Confirmer le SetupIntent avec les infos de la carte
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent.status === 'succeeded') {
        onCardSaved();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la carte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg bg-white">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
            loading
              ? 'bg-gray-300 text-gray-500'
              : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-80'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <FaLock className="w-4 h-4" />
              Enregistrer la carte
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Annuler
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
        <FaLock className="w-3 h-3" />
        Paiement sécurisé par Stripe
      </p>
    </form>
  );
};

// Composant pour gérer le 3DS
const Handle3DS = ({ clientSecret, invoiceId, clientId, onSuccess, onError }) => {
  const stripe = useStripe();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const confirm3DS = async () => {
      if (!stripe) return;

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

      if (error) {
        onError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        // Confirmer côté serveur
        try {
          const { data } = await api.post('/free-amount/confirm-payment', {
            clientId,
            invoiceId,
          });
          if (data.success) {
            onSuccess(data);
          } else {
            onError(data.message || 'Erreur lors de la confirmation');
          }
        } catch (err) {
          onError(err.response?.data?.message || 'Erreur lors de la confirmation');
        }
      }
      setProcessing(false);
    };

    confirm3DS();
  }, [stripe, clientSecret, invoiceId, clientId, onSuccess, onError]);

  if (processing) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-fideliz-blue border-t-transparent mb-4"></div>
        <p className="text-gray-600">Vérification 3D Secure en cours...</p>
      </div>
    );
  }

  return null;
};

// Composant principal du modal de paiement libre
const FreeAmountPaymentContent = ({
  clientId,
  sponsorId,
  sponsorName,
  userType,
  clientName,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const [step, setStep] = useState('amount'); // 'amount' | 'payment' | 'success'
  const [amount, setAmount] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [requires3DS, setRequires3DS] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Texte adaptatif selon le type d'utilisateur
  const sponsorLabel = userType === 'apport_affaires' ? "à l'apporteur d'affaires" : 'au parrain';
  const sponsorLabelShort = userType === 'apport_affaires' ? "apporteur" : 'parrain';

  // Calcul des montants avec commission Fideliz (10% + TVA 20%)
  const calculateAmounts = (giftCardAmount) => {
    const giftCard = parseFloat(giftCardAmount) || 0;
    const commissionHT = giftCard * 0.1; // 10% de commission
    const commissionTVA = commissionHT * 0.2; // TVA 20%
    const commissionTTC = commissionHT + commissionTVA;
    const totalTTC = giftCard + commissionTTC;
    return {
      giftCard,
      commissionHT,
      commissionTVA,
      commissionTTC,
      totalTTC,
    };
  };

  const amounts = calculateAmounts(amount);

  useEffect(() => {
    if (step === 'payment') {
      loadPaymentMethods();
    }
  }, [step]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/free-amount/payment-methods');
      setPaymentMethods(data.paymentMethods || []);
      if (data.paymentMethods?.length > 0) {
        setSelectedCard(data.paymentMethods[0].id);
      }
    } catch (err) {
      console.error('Erreur chargement cartes:', err);
      setError('Erreur lors du chargement des cartes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Supprimer cette carte ?')) return;

    try {
      await api.delete(`/free-amount/payment-methods/${cardId}`);
      setPaymentMethods(paymentMethods.filter((c) => c.id !== cardId));
      if (selectedCard === cardId) {
        setSelectedCard(paymentMethods[0]?.id || null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleCardSaved = () => {
    setShowAddCard(false);
    loadPaymentMethods();
  };

  const handleContinueToPayment = () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }
    setError('');
    setStep('payment');
  };

  const handlePay = async () => {
    if (!selectedCard || !amount) return;

    setPaying(true);
    setError('');

    try {
      const { data } = await api.post('/free-amount/pay', {
        clientId,
        paymentMethodId: selectedCard,
        amount: parseFloat(amount),
      });

      if (data.success && data.status === 'succeeded') {
        setSuccessData(data);
        setStep('success');
      } else if (data.status === 'requires_action') {
        // 3D Secure requis
        setRequires3DS({
          clientSecret: data.clientSecret,
          invoiceId: data.invoiceId,
        });
      } else {
        setError(data.message || 'Paiement non complété');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setPaying(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Autoriser uniquement les chiffres et un point décimal
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  // Étape 1: Saisie du montant
  if (step === 'amount') {
    return (
      <div className="space-y-6">
        {/* Infos sur le client et le parrain/apporteur */}
        <div className="bg-fideliz-blue/5 border border-fideliz-blue/20 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-fideliz-blue/10 p-2 rounded-full">
              <FaUser className="text-fideliz-blue w-4 h-4" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Client</p>
              <p className="font-semibold text-gray-900">{clientName}</p>
            </div>
          </div>
          <div className="border-t border-fideliz-blue/20 pt-3">
            <p className="text-sm text-gray-600 mb-1">
              Montant à verser <span className="font-medium">{sponsorLabel}</span> :
            </p>
            <p className="font-semibold text-fideliz-blue">{sponsorName}</p>
          </div>
        </div>

        {/* Champ montant */}
        <div>
          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Quel montant souhaitez-vous verser au {sponsorLabelShort} ?
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fideliz-blue focus:border-transparent font-medium pr-12"
              placeholder="0.00"
              autoFocus
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <FaEuroSign className="text-gray-400 w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Aperçu des montants */}
        {amount && parseFloat(amount) > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-3">Récapitulatif du paiement :</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Carte cadeau ({sponsorLabelShort})</span>
              <span className="font-medium">{amounts.giftCard.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Commission Fideliz (10%)</span>
              <span className="font-medium">{amounts.commissionHT.toFixed(2)} € HT</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span className="pl-4">TVA 20%</span>
              <span>{amounts.commissionTVA.toFixed(2)} €</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
              <span className="font-semibold text-gray-900">Total à payer</span>
              <span className="font-bold text-lg text-fideliz-blue">{amounts.totalTTC.toFixed(2)} € TTC</span>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          {amounts.giftCard.toFixed(2)} € seront crédités sur le solde de {sponsorName}.
          Une facture détaillée sera générée.
        </p>

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleContinueToPayment}
            disabled={!amount || parseFloat(amount) <= 0}
            className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
              !amount || parseFloat(amount) <= 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-80'
            }`}
          >
            Continuer vers le paiement
          </button>
        </div>
      </div>
    );
  }

  // Étape succès
  if (step === 'success') {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCheck className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Paiement réussi !</h3>
        <p className="text-gray-600 mb-2">
          {amounts.giftCard.toFixed(2)} € ont été crédités sur le compte de {sponsorName}.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Total payé : {amounts.totalTTC.toFixed(2)} € TTC (dont {amounts.commissionTTC.toFixed(2)} € de commission)
        </p>
        {successData?.invoiceUrl && (
          <a
            href={successData.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fideliz-blue hover:underline text-sm mb-4 block"
          >
            Voir la facture
          </a>
        )}
        <button
          onClick={() => onSuccess(successData)}
          className="w-full py-3 bg-fideliz-blue text-white font-semibold rounded-lg hover:bg-fideliz-blue hover:opacity-80"
        >
          Terminer
        </button>
      </div>
    );
  }

  // Étape 2: Paiement
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-fideliz-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Récapitulatif détaillé */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
        <p className="text-sm font-semibold text-gray-700 mb-3">Détail de la facture</p>

        {/* Ligne 1: Carte cadeau */}
        <div className="flex justify-between text-sm">
          <div>
            <span className="text-gray-700">Carte cadeau - {sponsorName}</span>
            <span className="ml-2 text-xs text-gray-400">(sans TVA)</span>
          </div>
          <span className="font-medium">{amounts.giftCard.toFixed(2)} €</span>
        </div>

        {/* Ligne 2: Commission */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-700">Commission Fideliz (10%)</span>
          <span className="font-medium">{amounts.commissionHT.toFixed(2)} € HT</span>
        </div>
        <div className="flex justify-between text-sm text-gray-500 pl-4">
          <span>TVA 20%</span>
          <span>{amounts.commissionTVA.toFixed(2)} €</span>
        </div>

        {/* Total */}
        <div className="border-t border-gray-300 pt-3 mt-3 flex justify-between items-center">
          <span className="font-bold text-gray-900">Total TTC</span>
          <span className="text-2xl font-bold text-amber-600">{amounts.totalTTC.toFixed(2)} €</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {requires3DS ? (
        <Handle3DS
          clientSecret={requires3DS.clientSecret}
          invoiceId={requires3DS.invoiceId}
          clientId={clientId}
          onSuccess={(data) => {
            setSuccessData(data);
            setStep('success');
            setRequires3DS(null);
          }}
          onError={(msg) => {
            setError(msg);
            setRequires3DS(null);
          }}
        />
      ) : showAddCard ? (
        <CardForm onCardSaved={handleCardSaved} onCancel={() => setShowAddCard(false)} />
      ) : (
        <>
          {/* Liste des cartes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choisissez une carte
            </label>

            {paymentMethods.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Aucune carte enregistrée
              </p>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => setSelectedCard(card.id)}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition ${
                      selectedCard === card.id
                        ? 'border-fideliz-blue bg-fideliz-blue/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FaCreditCard className="text-gray-500 w-6 h-6" />
                      <div>
                        <p className="font-medium capitalize">
                          {card.brand} **** {card.last4}
                        </p>
                        <p className="text-sm text-gray-500">
                          Expire {card.expMonth}/{card.expYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedCard === card.id && (
                        <FaCheck className="text-fideliz-blue" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card.id);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bouton ajouter une carte */}
            <button
              onClick={() => setShowAddCard(true)}
              className="mt-3 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-fideliz-blue hover:text-fideliz-blue flex items-center justify-center gap-2 transition"
            >
              <FaPlus className="w-4 h-4" />
              Ajouter une nouvelle carte
            </button>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setStep('amount')}
              className="px-6 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
            >
              Retour
            </button>
            <button
              onClick={handlePay}
              disabled={!selectedCard || paying}
              className={`flex-1 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 ${
                !selectedCard || paying
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {paying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Paiement en cours...
                </>
              ) : (
                <>
                  <FaLock className="w-5 h-5" />
                  Payer {amounts.totalTTC.toFixed(2)} € TTC
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Modal wrapper
const FreeAmountPaymentModal = ({
  isOpen,
  onClose,
  clientId,
  sponsorId,
  sponsorName,
  userType,
  clientName,
  onSuccess
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Valider et payer la commission
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FaTimes className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <Elements stripe={stripePromise}>
            <FreeAmountPaymentContent
              clientId={clientId}
              sponsorId={sponsorId}
              sponsorName={sponsorName}
              userType={userType}
              clientName={clientName}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default FreeAmountPaymentModal;
